import { Injectable } from '@angular/core';
import { Store, StoreConfig } from '@datorama/akita';
import { isNotNullish } from '@y42/util/nullish';
import {
  AclPermissionsState,
  AclRoleAssignmentsState,
  AclResourcesState,
  AclRolesState,
  AclRoleTooltipsState,
  AclResourceType,
  AclResource,
  AclPermission,
  AclPermissionsRecord,
  isAssetModuleType,
  AclPermissionsByResourceId,
  AclPermissionsBySpaceId,
  AclRoleAssignment,
} from '../models';

export interface AclState {
  permissions: AclPermissionsState;
  roleAssignments: AclRoleAssignmentsState;
  resources: AclResourcesState;
  roles: AclRolesState;
  roleTooltips: AclRoleTooltipsState;
}

const initialState: AclState = {
  permissions: {},
  roleAssignments: {},
  resources: {},
  roles: {} as AclRolesState,
  roleTooltips: {} as AclRoleTooltipsState,
};

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'acl' })
export class AclStore extends Store<AclState> {
  constructor() {
    super(initialState);
  }

  setRoles(roles: AclRolesState) {
    this.update({ roles });
  }

  setRoleTooltips(roleTooltips: AclRoleTooltipsState) {
    this.update({ roleTooltips });
  }

  // Stores the last response from an enumerate call without caring about the permisisons used to make the call
  // Should be only used for storing enumerated assets
  setResources({ resourceType, resources }: { resourceType: AclResourceType; resources: AclResource[] }) {
    this.update((st) => {
      return {
        resources: {
          ...st.resources,
          [resourceType]: resources,
        },
      };
    });
  }

  addPermissions({ resourceType, resources }: { resourceType: AclResourceType; resources: AclResource[] }) {
    this.update((st) => {
      const permissionsByResourceId = resources.reduce(
        (permissionsByResourceIdMap, resource) => {
          const resourcePermissionsMap = resource.permissions.reduce((permissionsMap, permission) => {
            permissionsMap[permission as AclPermission] = {
              allowed: true,
            };
            return permissionsMap;
          }, {} as AclPermissionsRecord);

          const resourcePermissionsMapWithSpaces =
            isAssetModuleType(resourceType) && isNotNullish(resource.space)
              ? {
                  [resource.space]: resourcePermissionsMap,
                }
              : resourcePermissionsMap;

          permissionsByResourceIdMap[resource.resource_id] = {
            ...permissionsByResourceIdMap[resource.resource_id],
            ...resourcePermissionsMapWithSpaces,
          };

          return permissionsByResourceIdMap;
        },
        {
          ...(st.permissions[resourceType] || {}),
        } as AclPermissionsByResourceId,
      );

      const permissionsByResourceType: AclPermissionsState = {
        ...st.permissions,
        [resourceType]: {
          ...permissionsByResourceId,
        },
      };

      return {
        permissions: {
          ...permissionsByResourceType,
        },
      };
    });
  }

  updatePermissions({
    resourceType,
    resourceId,
    permissionsMap,
    spaceId,
  }: {
    resourceType: AclResourceType;
    resourceId: string;
    permissionsMap: AclPermissionsRecord;
    spaceId?: string;
  }) {
    this.update((st) => {
      const resourcePermissionsMapWithSpaces =
        isAssetModuleType(resourceType) && isNotNullish(spaceId)
          ? {
              [spaceId]: {
                ...((st.permissions[resourceType]?.[resourceId] as AclPermissionsBySpaceId | undefined)?.[spaceId] ||
                  {}),
                ...permissionsMap,
              },
            }
          : permissionsMap;

      const permissionsByResourceType = {
        [resourceType]: {
          ...(st.permissions[resourceType] || {}),
          [resourceId]: {
            ...(st.permissions[resourceType]?.[resourceId] || {}),
            ...resourcePermissionsMapWithSpaces,
          },
        },
      };

      return {
        permissions: {
          ...st.permissions,
          ...permissionsByResourceType,
        },
      };
    });
  }

  updateRoleAssignments({
    resourceType,
    resourceId,
    roleAssignments,
  }: {
    resourceType: AclResourceType;
    resourceId: string;
    roleAssignments: AclRoleAssignment[];
  }) {
    this.update((st) => {
      const roleAssignmentsByResourceType = {
        [resourceType]: {
          ...(st.roleAssignments[resourceType] || {}),
          [resourceId]: roleAssignments,
        },
      };

      return {
        roleAssignments: {
          ...st.roleAssignments,
          ...roleAssignmentsByResourceType,
        },
      };
    });
  }

  resetAllRoleAssignments({ resourceId, resourceType }: { resourceId?: string; resourceType: AclResourceType }) {
    this.update((st) => {
      const roleAssignmentsByResourceId = resourceId
        ? {
            ...st.roleAssignments[resourceType],
            [resourceId]: undefined,
          }
        : {};

      const roleAssignmentsByResourceType = {
        [resourceType]: roleAssignmentsByResourceId,
      };

      return {
        roleAssignments: {
          ...st.roleAssignments,
          ...roleAssignmentsByResourceType,
        },
      };
    });
  }

  resetAllPermissions({ resourceType }: { resourceType?: AclResourceType } = {}): void {
    this.update((st) => {
      if (!resourceType) {
        return {
          permissions: {},
        };
      }

      const permissionsByResourceType = {
        [resourceType]: undefined,
      };

      return {
        permissions: {
          ...st.permissions,
          ...permissionsByResourceType,
        },
      };
    });
  }
}
